import { useState, type FormEvent } from "react";
import { Navigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useServerAuthPolicy } from "../../environments/primary/auth";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { useSession } from "../../shared/auth";
import { fetchAccountTeam } from "../../shared/auth/team";
import {
  canRemoveMember,
  deliveryNotice,
  fetchTeam,
  hasFreeSeat,
  inviteMember,
  inviteValidUntil,
  noSeatsMessage,
  removeMember,
  resendInvite,
  revokeInvite,
  seatsSummary,
  teamFailureMessage,
  teamRoleLabel,
  teamSectionAccess,
  type SentInvite,
  type TeamFailure,
  type TeamMember,
  type TeamResult,
} from "../../shared/team/team-admin";
import { Alert, AlertDescription } from "../ui/alert";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "./settingsLayout";

// Both queries answer for one account. Keyed by it, so a sign-out and a sign-in
// as someone else in this tab never show the first account's role or team.
const TEAM_QUERY_KEY = "team-admin";
const ACCOUNT_TEAM_QUERY_KEY = "account-team";

/** The signed-in account's team, as platform-api answers; undefined until it does. */
export function useAccountTeam() {
  const userId = useSession().data?.user.id ?? null;
  return useQuery({
    queryKey: [ACCOUNT_TEAM_QUERY_KEY, userId],
    queryFn: () => fetchAccountTeam(),
    enabled: userId !== null,
    staleTime: 60_000,
  }).data;
}

/** Whether this settings page offers the team section; see `teamSectionAccess`. */
export function useTeamSectionAccess() {
  const policy = useServerAuthPolicy((state) => state.policy);
  return teamSectionAccess(policy, useAccountTeam());
}

/** A team admin manages their team's people and invites, on the team's server. */
export function TeamSettingsPanel() {
  const access = useTeamSectionAccess();
  if (access === "hidden") return <Navigate to="/settings/general" replace />;
  if (access === "pending") return <TeamLoading />;
  return <TeamAdmin />;
}

function TeamLoading() {
  const { t } = useTranslation();
  return (
    <SettingsPageContainer>
      <SettingsSection title={t("settings/organization_zmd6s2")}>
        <SettingsRow title={t("settings/loading_gbm5n1")} description="" />
      </SettingsSection>
    </SettingsPageContainer>
  );
}

function TeamAdmin() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const selfId = useSession().data?.user.id ?? null;
  const teamQuery = useQuery({ queryKey: [TEAM_QUERY_KEY, selfId], queryFn: () => fetchTeam() });

  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{ failure: TeamFailure; retry: () => void } | null>(null);
  const [sent, setSent] = useState<SentInvite | null>(null);
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const [email, setEmail] = useState("");

  const run = async <T,>(action: () => Promise<TeamResult<T>>, onDone: (value: T) => void) => {
    setBusy(true);
    setFailure(null);
    const result = await action();
    setBusy(false);
    if (result.ok) onDone(result.value);
    else setFailure({ failure: result.failure, retry: () => void run(action, onDone) });
    void queryClient.invalidateQueries({ queryKey: [TEAM_QUERY_KEY, selfId] });
  };

  const handleInvite = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const input = { email: email.trim() };
    void run(
      () => inviteMember(input),
      (invite) => {
        setSent(invite);
        setEmail("");
      },
    );
  };
  const handleResend = (inviteId: string) => void run(() => resendInvite(inviteId), setSent);
  const handleRevoke = (inviteId: string) =>
    void run(
      () => revokeInvite(inviteId),
      () => setSent((current) => (current?.id === inviteId ? null : current)),
    );
  const handleRemove = (member: TeamMember) => {
    setRemoving(null);
    void run(
      () => removeMember(member.id),
      () => undefined,
    );
  };

  const loaded = teamQuery.data;
  if (loaded === undefined) return <TeamLoading />;
  if (!loaded.ok) {
    return (
      <SettingsPageContainer>
        <FailureAlert
          failure={loaded.failure}
          busy={teamQuery.isFetching}
          retry={() => void teamQuery.refetch()}
        />
      </SettingsPageContainer>
    );
  }
  const team = loaded.value;
  const freeSeat = hasFreeSeat(team);

  return (
    <SettingsPageContainer>
      {failure ? <FailureAlert {...failure} busy={busy} /> : null}

      <SettingsSection title={t("settings/seats_vl1ims")}>
        <SettingsRow
          title={seatsSummary(team)}
          description={t("settings/eachMemberAndEach_y2rwce")}
          className="pb-3.5"
        >
          <form className="mt-3 flex flex-col gap-3" onSubmit={handleInvite}>
            <div className="flex flex-col gap-1.5 sm:max-w-sm">
              <Label htmlFor="team-invite-email">{t("auth/emailAddress_1m22un")}</Label>
              <Input
                id="team-invite-email"
                type="email"
                autoComplete="off"
                required
                maxLength={320}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button type="submit" size="sm" disabled={busy || !freeSeat}>
                {t("settings/invite_ucu4g7")}
              </Button>
              <p className="text-xs text-muted-foreground">
                {freeSeat ? t("settings/thePersonGetsAn_7tih16") : noSeatsMessage()}
              </p>
            </div>
          </form>
          {sent ? (
            <DeliveryAlert invite={sent} busy={busy} resend={() => handleResend(sent.id)} />
          ) : null}
        </SettingsRow>
      </SettingsSection>

      <SettingsSection title={t("settings/invites_mno3aj")}>
        {team.invites.length === 0 ? (
          <SettingsRow title={t("settings/noPendingInvites_4t8afq")} description="" />
        ) : (
          team.invites.map((invite) => (
            <SettingsRow
              key={invite.id}
              title={invite.email ?? ""}
              description={inviteValidUntil(invite, i18n.language)}
              control={
                <>
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={busy}
                    onClick={() => handleResend(invite.id)}
                  >
                    {t("settings/sendAgain_z0fy02")}
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={busy}
                    onClick={() => handleRevoke(invite.id)}
                  >
                    {t("settings/revoke_57q94v")}
                  </Button>
                </>
              }
            />
          ))
        )}
      </SettingsSection>

      <SettingsSection title={t("settings/members_3jgora")}>
        {team.members.map((member) => (
          <SettingsRow
            key={member.id}
            title={member.name || member.email}
            description={`${member.email} · ${teamRoleLabel(member.role)}`}
            control={
              canRemoveMember(member, selfId) ? (
                <Button
                  size="xs"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setRemoving(member)}
                >
                  {t("settings/removeFromOrganization_7holci")}
                </Button>
              ) : null
            }
          />
        ))}
      </SettingsSection>

      <AlertDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("settings/removeFromTheOrganization_kswbqt", {
                name: removing?.name || removing?.email,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings/theyWillLoseAccess_xdxm0m")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              {t("common/cancel_8mt5fw")}
            </AlertDialogClose>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                if (removing) handleRemove(removing);
              }}
            >
              {t("settings/removeFromOrganization_7holci")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </SettingsPageContainer>
  );
}

/** A failed request; an outage, and only an outage, offers to try again. */
function FailureAlert({
  failure,
  retry,
  busy,
}: {
  failure: TeamFailure;
  retry: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Alert variant="error">
      <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span>{teamFailureMessage(failure)}</span>
        {failure.kind === "unavailable" ? (
          <Button size="xs" variant="outline" disabled={busy} onClick={retry}>
            {t("common/retry_9llvl9")}
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

function DeliveryAlert({
  invite,
  busy,
  resend,
}: {
  invite: SentInvite;
  busy: boolean;
  resend: () => void;
}) {
  const { t } = useTranslation();
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  const notice = deliveryNotice(invite);
  return (
    <Alert variant={notice.tone} className="mt-3">
      <AlertDescription className="flex flex-col gap-2">
        <span>{notice.message}</span>
        {notice.link ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 break-all rounded-md bg-muted px-2 py-1 text-xs select-all">
              {notice.link}
            </code>
            <div className="flex shrink-0 gap-2">
              <Button
                size="xs"
                variant="outline"
                onClick={() => copyToClipboard(notice.link ?? "", undefined)}
              >
                {isCopied ? t("common/copied_u7n02d") : t("chat/copyLink_zas6b6")}
              </Button>
              {notice.canResend ? (
                <Button size="xs" variant="outline" disabled={busy} onClick={resend}>
                  {t("settings/sendAgain_z0fy02")}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
